import React, { useState, useEffect, useCallback } from 'react';
import {
  Layout, Card, Typography, Button, Input, Table, Space,
  Popconfirm, message, Spin, Modal, Form, Select, Collapse, Tag,
} from 'antd';
import {
  ArrowLeftOutlined, PlusOutlined, DeleteOutlined, EditOutlined, MedicineBoxOutlined,
  ThunderboltOutlined, ImportOutlined,
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useDoctorContext } from './DoctorContext';
import { useLanguage } from './LanguageContext';
import API from '../config/api';
import { Medicine } from './type';
import { useDoctorSuggestions, MedicineTemplateData } from '../hooks/useDoctorSuggestions';

const { Header, Content } = Layout;
const { Title, Text } = Typography;
const { Option } = Select;
const { Panel } = Collapse;

const FREQUENCY_OPTIONS = [
  { value: '_',           label: '_' },
  { value: 'مرة واحدة',  label: 'مرة واحدة (Once)' },
  { value: 'مرتين',      label: 'مرتين (Twice)' },
  { value: '3 مرات',     label: '3 مرات (3×)' },
  { value: '4 مرات',     label: '4 مرات (4×)' },
  { value: 'يومياً',     label: 'يومياً (Daily)' },
];

const PERIOD_OPTIONS = [
  { value: 'يوميًا',            label: 'يوميًا' },
  { value: '1 يوم',             label: '1 يوم' },
  { value: '2 يوم',             label: '2 يوم' },
  { value: '3 يوم',             label: '3 يوم' },
  { value: '4 يوم',             label: '4 يوم' },
  { value: '5 يوم',             label: '5 يوم' },
  { value: '6 يوم',             label: '6 يوم' },
  { value: 'أسبوع',             label: 'أسبوع' },
  { value: 'أسبوعين',           label: 'أسبوعين' },
  { value: '3 اسابيع',          label: '3 اسابيع' },
  { value: 'شهر',               label: 'شهر' },
  { value: 'شهرياً',            label: 'شهرياً' },
  { value: 'يوم بعد يوم',       label: 'يوم بعد يوم' },
  { value: 'مرتان أسبوعياً',    label: 'مرتان أسبوعياً' },
  { value: '3 مرات أسبوعياً',   label: '3 مرات أسبوعياً' },
  { value: 'جرعة تدريجية',      label: 'جرعة تدريجية' },
];

const TIMING_OPTIONS = [
  { value: '_',          label: '_' },
  { value: 'قبل النوم',  label: 'قبل النوم' },
  { value: 'بعد الأكل',  label: 'بعد الأكل' },
  { value: 'قبل الأكل',  label: 'قبل الأكل' },
  { value: 'بعد الفطار', label: 'بعد الفطار' },
  { value: 'بعد الغداء', label: 'بعد الغداء' },
  { value: 'بعد العشاء', label: 'بعد العشاء' },
];

const FORM_OPTIONS = [
  { value: 'Tab',      label: 'Tab (Tablet)' },
  { value: 'Capsule',  label: 'Capsule' },
  { value: 'Sachets',  label: 'Sachets' },
  { value: 'Amp',      label: 'Amp (Ampoule)' },
  { value: 'Patch',    label: 'Patch' },
  { value: 'EyeDrops', label: 'Eye drops (قطرات عين)' },
  { value: 'UnderTongue', label: 'Under the tongue (تحت اللسان)' },
];

const DOSE_QTY_OPTIONS = [
  { value: '_', label: '_' },
  { value: '1', label: '1' },
  { value: '2', label: '2' },
  { value: '3', label: '3' },
  { value: '4', label: '4' },
];

const ROUTE_OPTIONS = [
  { value: 'Oral',        label: 'Oral' },
  { value: 'Iv',          label: 'IV' },
  { value: 'Im',          label: 'IM' },
  { value: 'Spray',       label: 'Spray' },
  { value: 'Ointment',    label: 'Ointment' },
  { value: 'Sublingual',  label: 'Sublingual' },
  { value: 'Lingual',     label: 'Lingual' },
];

/** Render a small summary of saved template fields */
function TemplateSummary({ tpl }: { tpl: MedicineTemplateData | null }) {
  if (!tpl || !Object.keys(tpl).length) return null;
  const parts = [
    tpl.form,
    tpl.frequency,
    tpl.period,
    tpl.timing,
    tpl.route,
    tpl.doseQty,
    tpl.medicineDetails,
  ].filter(Boolean);
  return (
    <div style={{ marginTop: 2 }}>
      {parts.map(p => (
        <Tag key={p} color="blue" style={{ marginBottom: 2, fontSize: 11 }}>{p}</Tag>
      ))}
    </div>
  );
}

const MedicinesManagement: React.FC = () => {
  const navigate = useNavigate();
  const { t, isRTL } = useLanguage();
  const { doctorId: ctxDoctorId } = useDoctorContext();
  const doctorId = ctxDoctorId || localStorage.getItem('doctorId');
  const { getMedicineTemplate, saveMedicineTemplate, deleteMedicineTemplate } = useDoctorSuggestions(doctorId ?? '');

  const [medicines, setMedicines] = useState<Medicine[]>([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  // Trigger re-render after template save
  const [templateRevision, setTemplateRevision] = useState(0);

  // Add medicine
  const [newName, setNewName] = useState('');

  // Edit medicine modal
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editingMedicine, setEditingMedicine] = useState<Medicine | null>(null);
  const [editForm] = Form.useForm();

  // Medicine Hub import modal
  const [hubModalOpen, setHubModalOpen] = useState(false);
  const [hubSpecialties, setHubSpecialties] = useState<{ _id: string; specialty_name: string; count: number }[]>([]);
  const [hubSpecialtyId, setHubSpecialtyId] = useState<string | undefined>(undefined);
  const [hubLoading, setHubLoading] = useState(false);
  const [hubImporting, setHubImporting] = useState(false);
  const [hubResult, setHubResult] = useState<{ imported: number; skipped: number } | null>(null);

  const openHubModal = async () => {
    setHubModalOpen(true);
    setHubResult(null);
    setHubSpecialtyId(undefined);
    if (hubSpecialties.length > 0) return;
    setHubLoading(true);
    try {
      const res = await axios.get(`${API.BASE_URL}${API.ENDPOINTS.MEDICINE_HUB_SPECIALTIES}`);
      if (res.data?.success) setHubSpecialties(res.data.specialties || []);
    } catch {
      message.error(t('medicineHubImportFailed'));
    } finally {
      setHubLoading(false);
    }
  };

  const handleHubImport = async () => {
    if (!hubSpecialtyId || !doctorId) return;
    setHubImporting(true);
    setHubResult(null);
    try {
      const res = await axios.post(
        `${API.BASE_URL}${API.ENDPOINTS.MEDICINE_HUB_IMPORT(doctorId)}`,
        { specialty_id: hubSpecialtyId }
      );
      if (res.data?.success) {
        setHubResult({ imported: res.data.imported, skipped: res.data.skipped });
        if (res.data.imported > 0) {
          fetchMedicines();
        }
      } else {
        message.error(t('medicineHubImportFailed'));
      }
    } catch {
      message.error(t('medicineHubImportFailed'));
    } finally {
      setHubImporting(false);
    }
  };

  const fetchMedicines = useCallback(async () => {
    if (!doctorId) return;
    setLoading(true);
    try {
      const res = await axios.get(`${API.BASE_URL}${API.ENDPOINTS.DOCTOR_MEDICINES(doctorId)}`);
      if (res.data?.success) {
        setMedicines(res.data.medicines || []);
      }
    } catch {
      message.error(t('error'));
    } finally {
      setLoading(false);
    }
  }, [doctorId, t]);

  useEffect(() => {
    fetchMedicines();
  }, [fetchMedicines]);

  const handleAdd = async () => {
    const trimmed = newName.trim();
    if (!trimmed) {
      message.warning(t('medicineNameRequired'));
      return;
    }
    setSaving(true);
    try {
      const res = await axios.post(`${API.BASE_URL}${API.ENDPOINTS.DOCTOR_MEDICINES(doctorId!)}`, { name: trimmed });
      if (res.data?.success) {
        setMedicines(prev => [...prev, res.data.medicine].sort((a, b) => a.name.localeCompare(b.name)));
        setNewName('');
        message.success(t('medicineAdded'));
      }
    } catch (err: any) {
      const msg = err?.response?.data?.message;
      if (msg === 'Medicine already exists') {
        message.warning(t('medicineAlreadyExists'));
      } else {
        message.error(t('medicineAddFailed'));
      }
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (medicine: Medicine) => {
    setSaving(true);
    try {
      await axios.delete(`${API.BASE_URL}${API.ENDPOINTS.MEDICINE(doctorId!, medicine._id)}`);
      setMedicines(prev => prev.filter(m => m._id !== medicine._id));
      deleteMedicineTemplate(medicine.name);
      message.success(t('medicineDeleted'));
    } catch {
      message.error(t('medicineDeleteFailed'));
    } finally {
      setSaving(false);
    }
  };

  const openEditModal = (medicine: Medicine) => {
    setEditingMedicine(medicine);
    const tpl = getMedicineTemplate(medicine.name) || {};
    editForm.setFieldsValue({
      name: medicine.name,
      tpl_frequency: tpl.frequency || undefined,
      tpl_period:    tpl.period    || undefined,
      tpl_timing:    tpl.timing    || undefined,
      tpl_form:      tpl.form      || undefined,
      tpl_route:     tpl.route     || undefined,
      tpl_doseQty:   tpl.doseQty   || undefined,
      tpl_medicineDetails: tpl.medicineDetails || undefined,
    });
    setEditModalOpen(true);
  };

  const handleEditSubmit = async (values: {
    name: string;
    tpl_frequency?: string;
    tpl_period?: string;
    tpl_timing?: string;
    tpl_form?: string;
    tpl_route?: string;
    tpl_doseQty?: string;
    tpl_medicineDetails?: string;
  }) => {
    if (!editingMedicine) return;
    setSaving(true);
    try {
      const res = await axios.put(
        `${API.BASE_URL}${API.ENDPOINTS.MEDICINE(doctorId!, editingMedicine._id)}`,
        { name: values.name.trim() }
      );
      if (res.data?.success) {
        // If the name changed, remove the old template key
        if (editingMedicine.name.toLowerCase() !== values.name.trim().toLowerCase()) {
          deleteMedicineTemplate(editingMedicine.name);
        }
        // Save template defaults
        saveMedicineTemplate(values.name.trim(), {
          frequency: values.tpl_frequency,
          period:    values.tpl_period,
          timing:    values.tpl_timing,
          form:      values.tpl_form,
          route:     values.tpl_route,
          doseQty:   values.tpl_doseQty,
          medicineDetails: values.tpl_medicineDetails,
        });
        setMedicines(prev =>
          prev
            .map(m => (m._id === editingMedicine._id ? res.data.medicine : m))
            .sort((a, b) => a.name.localeCompare(b.name))
        );
        setTemplateRevision(r => r + 1);
        message.success(t('medicineUpdated'));
        setEditModalOpen(false);
        setEditingMedicine(null);
      }
    } catch {
      message.error(t('medicineUpdateFailed'));
    } finally {
      setSaving(false);
    }
  };

  const columns = [
    {
      title: t('medicineName'),
      dataIndex: 'name',
      key: 'name',
      render: (name: string) => (
        <div>
          <Text style={{ fontSize: 15 }}>{name}</Text>
          <TemplateSummary tpl={getMedicineTemplate(name)} key={`${name}-${templateRevision}`} />
        </div>
      ),
    },
    {
      title: t('actions'),
      key: 'actions',
      width: 120,
      render: (_: any, record: Medicine) => (
        <Space>
          <Button
            type="text"
            icon={<EditOutlined />}
            onClick={() => openEditModal(record)}
          />
          <Popconfirm
            title={`${t('delete')} "${record.name}"?`}
            onConfirm={() => handleDelete(record)}
            okText={t('delete')}
            cancelText={t('cancel')}
            okButtonProps={{ danger: true }}
          >
            <Button type="text" danger icon={<DeleteOutlined />} loading={saving} />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <Layout style={{ minHeight: '100vh', direction: isRTL ? 'rtl' : 'ltr' }}>
      <Header
        style={{
          background: 'white',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          padding: '0 16px',
          borderBottom: '1px solid #f0f0f0',
        }}
      >
        <Button
          icon={<ArrowLeftOutlined style={{ transform: isRTL ? 'scaleX(-1)' : 'none' }} />}
          onClick={() => navigate('/dashboard')}
          type="text"
        >
          {t('backToDashboard')}
        </Button>
        <Title level={4} style={{ margin: 0 }}>
          <MedicineBoxOutlined style={{ marginInlineEnd: 8 }} />
          {t('medicinesManagement')}
        </Title>
      </Header>

      <Content style={{ margin: 24, maxWidth: 700, marginInline: 'auto', width: '100%' }}>
        <Card>
          {/* Add new medicine */}
          <div style={{ marginBottom: 24 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
              <Title level={5} style={{ margin: 0 }}>{t('addMedicine')}</Title>
              <Button
                icon={<ImportOutlined />}
                onClick={openHubModal}
              >
                {t('medicineHubImport')}
              </Button>
            </div>
            <Space.Compact style={{ width: '100%' }}>
              <Input
                placeholder={t('medicineNamePlaceholder')}
                value={newName}
                onChange={(e) => setNewName(e.target.value)}
                onPressEnter={handleAdd}
                disabled={saving}
                style={{ flex: 1 }}
              />
              <Button
                type="primary"
                icon={<PlusOutlined />}
                onClick={handleAdd}
                loading={saving}
              >
                {t('add')}
              </Button>
            </Space.Compact>
            <div style={{ marginTop: 8, color: '#888', fontSize: 12 }}>
              After adding, click <EditOutlined /> to configure default frequency, form, and dosing for instant auto-fill in prescriptions.
            </div>
          </div>

          {/* Medicines list */}
          {loading ? (
            <div style={{ textAlign: 'center', padding: 40 }}>
              <Spin size="large" />
            </div>
          ) : medicines.length === 0 ? (
            <div style={{ textAlign: 'center', padding: 40, color: '#999' }}>
              {t('noMedicines')}
            </div>
          ) : (
            <Table
              dataSource={medicines}
              columns={columns}
              rowKey="_id"
              pagination={{ pageSize: 20, showSizeChanger: false }}
              size="middle"
            />
          )}
        </Card>
      </Content>

      {/* Medicine Hub Import Modal */}
      <Modal
        title={
          <Space>
            <ImportOutlined />
            {t('medicineHubTitle')}
          </Space>
        }
        open={hubModalOpen}
        onCancel={() => { setHubModalOpen(false); setHubResult(null); }}
        style={{ direction: isRTL ? 'rtl' : 'ltr' }}
        width={440}
        footer={[
          <Button key="cancel" onClick={() => { setHubModalOpen(false); setHubResult(null); }}>
            {t('cancel')}
          </Button>,
          <Button
            key="import"
            type="primary"
            icon={<ImportOutlined />}
            loading={hubImporting}
            disabled={!hubSpecialtyId}
            onClick={handleHubImport}
          >
            {t('medicineHubImportBtn')}
          </Button>,
        ]}
      >
        <p style={{ color: '#555', marginBottom: 16 }}>{t('medicineHubDesc')}</p>

        {hubLoading ? (
          <div style={{ textAlign: 'center', padding: 24 }}><Spin /></div>
        ) : hubSpecialties.length === 0 ? (
          <div style={{ textAlign: 'center', color: '#999', padding: 16 }}>{t('medicineHubEmpty')}</div>
        ) : (
          <Select
            style={{ width: '100%' }}
            placeholder={t('medicineHubSelectSpecialty')}
            value={hubSpecialtyId}
            onChange={val => { setHubSpecialtyId(val); setHubResult(null); }}
            showSearch
            optionFilterProp="children"
          >
            {hubSpecialties.map(s => (
              <Option key={s._id} value={s._id}>
                {s.specialty_name}
                <span style={{ color: '#aaa', fontSize: 12, marginInlineStart: 6 }}>({s.count})</span>
              </Option>
            ))}
          </Select>
        )}

        {hubResult && (
          <div style={{
            marginTop: 16, padding: '10px 14px',
            background: '#f6ffed', border: '1px solid #b7eb8f', borderRadius: 6,
            color: '#389e0d', fontSize: 14,
          }}>
            ✅ {t('medicineHubImportSuccess')
              .replace('{imported}', String(hubResult.imported))
              .replace('{skipped}', String(hubResult.skipped))}
          </div>
        )}
      </Modal>

      {/* Edit Medicine Modal */}
      <Modal
        title={
          <Space>
            <EditOutlined />
            {t('editMedicine')}
          </Space>
        }
        open={editModalOpen}
        onCancel={() => { setEditModalOpen(false); setEditingMedicine(null); }}
        footer={null}
        style={{ direction: isRTL ? 'rtl' : 'ltr' }}
        width={520}
      >
        <Form
          form={editForm}
          layout="vertical"
          onFinish={handleEditSubmit}
        >
          <Form.Item
            name="name"
            label={t('medicineName')}
            rules={[{ required: true, message: t('medicineNameRequired') }]}
          >
            <Input placeholder={t('medicineNamePlaceholder')} />
          </Form.Item>

          {/* Pre-configured defaults for prescriptions */}
          <Collapse
            ghost
            defaultActiveKey={['defaults']}
            style={{ marginBottom: 16, border: '1px solid #f0f0f0', borderRadius: 8 }}
          >
            <Panel
              header={
                <Space>
                  <ThunderboltOutlined style={{ color: '#1890ff' }} />
                  <span style={{ fontWeight: 500 }}>Auto-fill Defaults</span>
                  <span style={{ color: '#888', fontSize: 12 }}>
                    — When this medicine is selected in a prescription, these values fill in automatically
                  </span>
                </Space>
              }
              key="defaults"
            >
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 12px' }}>
                <Form.Item name="tpl_form" label="Form (شكل الدواء)">
                  <Select placeholder="e.g. Tab, Capsule…" allowClear>
                    {FORM_OPTIONS.map(o => <Option key={o.value} value={o.value}>{o.label}</Option>)}
                  </Select>
                </Form.Item>
                <Form.Item name="tpl_route" label="Route (طريقة الإعطاء)">
                  <Select placeholder="e.g. Oral, IV…" allowClear>
                    {ROUTE_OPTIONS.map(o => <Option key={o.value} value={o.value}>{o.label}</Option>)}
                  </Select>
                </Form.Item>
                <Form.Item name="tpl_frequency" label="Frequency (التكرار)">
                  <Select placeholder="e.g. مرتين…" allowClear>
                    {FREQUENCY_OPTIONS.map(o => <Option key={o.value} value={o.value}>{o.label}</Option>)}
                  </Select>
                </Form.Item>
                <Form.Item name="tpl_period" label="Period (المدة)">
                  <Select placeholder="e.g. أسبوع…" allowClear>
                    {PERIOD_OPTIONS.map(o => <Option key={o.value} value={o.value}>{o.label}</Option>)}
                  </Select>
                </Form.Item>
                <Form.Item name="tpl_timing" label="Timing (التوقيت)" style={{ gridColumn: '1 / -1' }}>
                  <Select placeholder="e.g. بعد الأكل…" allowClear>
                    {TIMING_OPTIONS.map(o => <Option key={o.value} value={o.value}>{o.label}</Option>)}
                  </Select>
                </Form.Item>
                <Form.Item name="tpl_doseQty" label="Dose amount (كمية الجرعة)" style={{ gridColumn: '1 / -1' }}>
                  <Select placeholder="e.g. 1…" allowClear>
                    {DOSE_QTY_OPTIONS.map(o => <Option key={o.value} value={o.value}>{o.label}</Option>)}
                  </Select>
                </Form.Item>
                <Form.Item
                  name="tpl_medicineDetails"
                  label="Medicine details (تفاصيل الأدوية)"
                  style={{ gridColumn: '1 / -1' }}
                >
                  <Input.TextArea rows={2} placeholder="Optional notes saved with this medicine default…" />
                </Form.Item>
              </div>
            </Panel>
          </Collapse>

          <Form.Item style={{ marginBottom: 0, textAlign: 'end' }}>
            <Space>
              <Button onClick={() => { setEditModalOpen(false); setEditingMedicine(null); }}>
                {t('cancel')}
              </Button>
              <Button type="primary" htmlType="submit" loading={saving}>
                {t('save')}
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
    </Layout>
  );
};

export default MedicinesManagement;
